import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { PincodeLookupController } from '../../../common-files/pincode-lookup';

// create a new property + its rooms and assign them to the utsav in one go.
// data = { utsavId }. self-saving; closes true on success.
@Component({
  selector: 'app-utsav-new-property-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    LoaderComponent,
  ],
  templateUrl: './utsav-new-property-dialog.component.html',
  styleUrls: ['./utsav-new-property-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class UtsavNewPropertyDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading: boolean = false;
  readonly pincodeLookup: PincodeLookupController;

  roomTypes = ['HALL', 'ROOM', 'DORMITORY'];
  genders = ['MALE', 'FEMALE', 'BOTH'];
  // INTERNAL = on the mandir campus, EXTERNAL = rented / outside building
  readonly categoryOptions = [
    { value: 'INTERNAL', label: 'Internal (on campus)' },
    { value: 'EXTERNAL', label: 'External (outside)' },
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<UtsavNewPropertyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { utsavId: number },
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private advanceSearchService: AdvanceSearchService
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      category: ['INTERNAL', Validators.required],
      address: [''],
      state: [''],
      district: [''],
      pincode: ['', Validators.pattern(/^\d{6}$/)],
      landmark: [''],
      locationUrl: [''],
      latitude: [null as number | null, [Validators.min(-90), Validators.max(90)]],
      longitude: [null as number | null, [Validators.min(-180), Validators.max(180)]],
      contact: ['', Validators.pattern(/^\d{10}$/)],
      altContact: ['', Validators.pattern(/^\d{10}$/)],
      rooms: this.fb.array([this.newRoom()]),
    });
    this.pincodeLookup = new PincodeLookupController(
      this.form,
      this.advanceSearchService
    );
  }

  ngOnInit(): void {
    this.pincodeLookup.start();
  }

  ngOnDestroy(): void {
    this.pincodeLookup.destroy();
  }

  get pincodeControl() {
    return this.pincodeLookup.pincodeControl;
  }

  get isPincodeLoading(): boolean {
    return this.pincodeLookup.isLoading;
  }

  get pincodeLookupStatus() {
    return this.pincodeLookup.status;
  }

  get pincodeLookupMessage(): string {
    return this.pincodeLookup.message;
  }

  get stateList(): string[] {
    return this.pincodeLookup.stateList;
  }

  get districtList(): string[] {
    return this.pincodeLookup.districtList;
  }

  onDistrictChange(): void {
    this.pincodeLookup.resolveSelectedDistrict();
  }

  get rooms(): FormArray {
    return this.form.get('rooms') as FormArray;
  }

  private newRoom(): FormGroup {
    return this.fb.group({
      roomNumber: ['', Validators.required],
      floor: [''],
      capacity: [null, [Validators.required, Validators.min(1)]],
      roomType: ['ROOM', Validators.required],
      gender: ['BOTH', Validators.required],
    });
  }

  addRoomRow(): void {
    this.rooms.push(this.newRoom());
  }

  removeRoomRow(index: number): void {
    if (this.rooms.length > 1) this.rooms.removeAt(index);
  }

  submit(): void {
    const pincode = String(this.form.get('pincode')?.value ?? '');
    if (!this.pincodeLookup.isVerified(pincode)) {
      this.form.get('pincode')?.markAsTouched();
      this.toastService.error(
        this.pincodeLookupMessage || 'Enter a valid, verified 6-digit pincode.'
      );
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.error('Property name and each room (number, capacity, type, gender) are required.');
      return;
    }

    // coordinates are optional but must come as a pair.
    const latitude = this.form.get('latitude')?.value;
    const longitude = this.form.get('longitude')?.value;
    const hasLatitude = latitude !== null && latitude !== undefined && `${latitude}` !== '';
    const hasLongitude = longitude !== null && longitude !== undefined && `${longitude}` !== '';
    if (hasLatitude !== hasLongitude) {
      this.toastService.error('Latitude and Longitude must be provided together.');
      return;
    }

    this.isLoading = true;
    this.establishmentService.addNewPropertyToUtsav(this.data.utsavId, this.form.value).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Property created and assigned.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to create property.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
